import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Button,
  Tag,
  Spin,
  message,
  Avatar,
  Descriptions,
  Row,
  Col,
  Empty,
  Image,
  Space,
  Switch,
  Modal,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  SyncOutlined,
  ArrowRightOutlined,
  BankOutlined,
  UserOutlined,
  ProjectOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { recurringExpensesApi } from '../../utils/api';
import { formatMoneyFa } from '../../utils/formatPriceToman';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';

const toPersianNumber = (num) => {
  if (num === null || num === undefined) return '-';
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return String(num).replace(/\d/g, (d) => persianDigits[parseInt(d)]);
};

const rotationLabels = {
  daily: 'روزانه',
  weekly: 'هفتگی',
  'bi-weekly': 'دو هفته‌ای',
  monthly: 'ماهانه',
  quarterly: 'سه ماهه',
  'half-yearly': 'شش ماهه',
  annually: 'سالانه',
};

const ViewRecurringExpense = () => {
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const { id } = useParams();
  const { canEdit, canDelete } = useResourcePermissions('expenses');
  const [loading, setLoading] = useState(true);
  const [expense, setExpense] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const goBackToList = () => {
    navigate(returnToRef.current || '/finance/recurring-expenses', { state: buildGoBackState() });
  };

  useEffect(() => {
    fetchExpense();
  }, [id]);

  const fetchExpense = async () => {
    setLoading(true);
    try {
      const res = await recurringExpensesApi.getById(id);
      if (res?.data?.expense) {
        setExpense(res.data.expense);
      }
    } catch (error) {
      console.error('Error fetching recurring expense:', error);
      message.error('خطا در دریافت اطلاعات هزینه دوره‌ای');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return toPersianNumber(date.toLocaleDateString('fa-IR'));
  };

  const handleStatusChange = async (checked) => {
    const newStatus = checked ? 'active' : 'inactive';
    setStatusLoading(true);
    try {
      await recurringExpensesApi.updateStatus(id, newStatus);
      message.success('وضعیت هزینه دوره‌ای با موفقیت تغییر کرد');
      fetchExpense();
    } catch (error) {
      message.error('خطا در تغییر وضعیت');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>حذف هزینه دوره‌ای</span>,
      content: <span style={{ fontFamily: 'CustomFont' }}>آیا از حذف این هزینه دوره‌ای اطمینان دارید؟</span>,
      okText: 'بله، حذف کن',
      cancelText: 'لغو',
      okButtonProps: { danger: true, style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' } },
      cancelButtonProps: { style: { borderColor: '#d9d9d9', color: '#595959' } },
      onOk: async () => {
        try {
          await recurringExpensesApi.delete(id);
          message.success('هزینه دوره‌ای با موفقیت حذف شد');
          navigate(returnToRef.current || '/finance/recurring-expenses', { state: buildGoBackState() });
        } catch (error) {
          message.error('خطا در حذف هزینه دوره‌ای');
        }
      },
    });
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  if (!expense) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center h-64">
          <Empty description={<span style={{ fontFamily: 'CustomFont' }}>هزینه دوره‌ای یافت نشد</span>} />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center">
                <SyncOutlined className="text-purple-600 dark:text-purple-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0" style={{ fontFamily: 'CustomFont' }}>
                  {expense.item_name}
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                  جزئیات هزینه دوره‌ای
                </p>
              </div>
            </div>
            <Space wrap>
              {canEdit ? (
                <Switch
                  checked={expense.status === 'active'}
                  onChange={handleStatusChange}
                  loading={statusLoading}
                  checkedChildren="فعال"
                  unCheckedChildren="غیرفعال"
                  style={{ fontFamily: 'CustomFont' }}
                />
              ) : null}
              {canEdit ? (
                <Button
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/finance/recurring-expenses/${id}/edit`)}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  ویرایش
                </Button>
              ) : null}
              {canDelete ? (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDelete}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  حذف
                </Button>
              ) : null}
              <Button
                icon={<ArrowRightOutlined />}
                onClick={goBackToList}
                style={{ fontFamily: 'CustomFont' }}
              >
                بازگشت
              </Button>
            </Space>
          </div>

          {/* Content */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={16}>
                <Card className="dark:bg-gray-800 border-0 shadow-sm h-full" styles={{ body: { fontFamily: 'CustomFont' } }}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center">
                      <SyncOutlined className="text-purple-600 text-2xl" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold m-0 dark:text-white" style={{ fontFamily: 'CustomFont' }}>
                        {expense.item_name}
                      </h2>
                      <div className="mt-1 flex items-center gap-2">
                        <Tag
                          color={expense.status === 'active' ? 'success' : 'default'}
                          style={{ fontFamily: 'CustomFont', fontSize: '14px', padding: '4px 12px' }}
                        >
                          {expense.status === 'active' ? 'فعال' : 'غیرفعال'}
                        </Tag>
                        <Tag color="blue" style={{ fontFamily: 'CustomFont', fontSize: '14px', padding: '4px 12px' }}>
                          {rotationLabels[expense.rotation] || expense.rotation}
                        </Tag>
                      </div>
                    </div>
                  </div>

                  <Descriptions
                    column={{ xs: 1, sm: 2 }}
                    bordered
                    size="small"
                    labelStyle={{ fontFamily: 'CustomFont' }}
                    contentStyle={{ fontFamily: 'CustomFont' }}
                  >
                    <Descriptions.Item label="مبلغ">
                      <span className="text-blue-600 font-bold text-lg">
                        {formatMoneyFa(expense.price, expense.currency?.currency_symbol)}
                      </span>
                    </Descriptions.Item>
                    <Descriptions.Item label="تناوب">
                      <Tag color="blue" style={{ fontFamily: 'CustomFont' }}>
                        {rotationLabels[expense.rotation] || expense.rotation}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="تاریخ شروع">
                      <Space>
                        <CalendarOutlined />
                        {formatDate(expense.issue_date)}
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="هزینه بعدی">
                      <Space>
                        <CalendarOutlined />
                        {formatDate(expense.next_expense_date)}
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="خریداری از">
                      {expense.purchase_from || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="کارمند">
                      {expense.user ? (
                        <Space>
                          <Avatar src={expense.user.image_url} size="small" icon={<UserOutlined />}>
                            {expense.user.name?.[0]}
                          </Avatar>
                          {expense.user.name}
                        </Space>
                      ) : (
                        '-'
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="پروژه">
                      {expense.project ? (
                        <Space>
                          <ProjectOutlined />
                          {expense.project.project_name}
                        </Space>
                      ) : (
                        '-'
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="حساب بانکی">
                      {expense.bank ? (
                        <Space>
                          <BankOutlined />
                          {expense.bank.bank_name || 'صندوق نقدی'}
                        </Space>
                      ) : (
                        '-'
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>

              <Col xs={24} lg={8}>
                <Card
                  title={<span style={{ fontFamily: 'CustomFont' }}>فاکتور / رسید</span>}
                  className="dark:bg-gray-800 border-0 shadow-sm h-full"
                  styles={{ body: { fontFamily: 'CustomFont', textAlign: 'center' } }}
                >
                  {expense.bill_url && expense.bill_url !== '' ? (
                    expense.bill_url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                      <Image src={expense.bill_url} className="rounded-lg max-w-full" />
                    ) : (
                      <a
                        href={expense.bill_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 text-lg"
                        style={{ fontFamily: 'CustomFont' }}
                      >
                        دانلود / مشاهده فایل
                      </a>
                    )
                  ) : (
                    <Empty description={<span style={{ fontFamily: 'CustomFont' }}>فاکتوری آپلود نشده است</span>} />
                  )}
                </Card>
              </Col>
            </Row>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ViewRecurringExpense;
